// ============================================
// Creator Module - Example Dialogue (대화 예시)
// 인트로 편집기와 유사한 UI로 대화 예시 편집
// ============================================

import { showToast, escapeHtml } from '../utils.js';
import {
  getExampleDialogues, setExampleDialogues,
  addExampleDialogue, updateExampleDialogue, removeExampleDialogue,
  setEditingExampleIndex, getEditingExampleIndex,
  getCurrentEditorData
} from './state.js';
import { renderExampleDialogueList } from './editor/form.js';
import { updateTabCheckStatus, updateExampleDialogueTotalCount } from './editor/events.js';
import { getMainImageUrl } from './editor.js';

// 최대 예시 개수 및 글자 수
const MAX_EXAMPLES = 10;
const MAX_TOTAL_CHARS = 2000;

// 현재 편집 중인 블록들
let currentExampleBlocks = [];
let currentExampleType = 'dialogue';
let selectedExampleBlockIndex = null;
let editingBlockIndex = null; // 편집 중인 블록 인덱스 (원래 위치 유지용)
let eventsInitialized = false;

// ============================================
// 모달 열기/닫기
// ============================================

export function openExampleDialogueModal(editIndex = null) {
  const dialogues = getExampleDialogues();

  // 새 예시 추가인 경우 개수 체크
  if (editIndex === null && dialogues.length >= MAX_EXAMPLES) {
    showToast(`대화 예시는 최대 ${MAX_EXAMPLES}개까지 추가할 수 있습니다`);
    return;
  }

  // 새 예시 추가인 경우 총 글자 수 체크
  if (editIndex === null) {
    const totalChars = getTotalExampleChars();
    if (totalChars >= MAX_TOTAL_CHARS) {
      showToast(`대화 예시는 총 ${MAX_TOTAL_CHARS}자까지 작성할 수 있습니다`);
      return;
    }
  }

  setEditingExampleIndex(editIndex);

  // 편집 모드인 경우 기존 블록 로드
  if (editIndex !== null && dialogues[editIndex]) {
    currentExampleBlocks = [...dialogues[editIndex].blocks];
  } else {
    currentExampleBlocks = [];
  }

  currentExampleType = 'dialogue';
  selectedExampleBlockIndex = null;
  editingBlockIndex = null;

  // 모달 HTML 생성 및 표시
  showExampleModal();
}

function showExampleModal() {
  const modal = document.getElementById('exampleDialogueEditModal');
  if (!modal) return;

  // 타이틀 업데이트
  updateExampleModalTitle();

  // 글자 수 업데이트
  updateExampleCharCount();

  // 타입 탭 초기화
  currentExampleType = 'dialogue';
  modal.querySelectorAll('#exampleTypeTabs .intro-type-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.exampleType === 'dialogue');
  });

  // 미리보기 렌더링
  renderExamplePreview();

  // 입력창 초기화
  const input = document.getElementById('exampleComposerInput');
  if (input) {
    input.value = '';
  }

  // 이벤트 바인딩 (최초 1회만)
  if (!eventsInitialized) {
    bindExampleModalEvents();
    eventsInitialized = true;
  }

  // 모달 표시 (CSS transform 애니메이션 사용)
  modal.offsetHeight; // reflow
  modal.classList.add('active');

  if (window.ModalManager) {
    window.ModalManager.push('exampleDialogueEditModal');
  }
}

function updateExampleModalTitle() {
  const titleEl = document.getElementById('exampleModalTitle');
  if (!titleEl) return;

  const editIndex = getEditingExampleIndex();
  const dialogues = getExampleDialogues();

  if (editIndex !== null) {
    titleEl.textContent = `대화 예시 ${editIndex + 1}`;
  } else {
    titleEl.textContent = `대화 예시 ${dialogues.length + 1}`;
  }
}

function updateExampleCharCount() {
  const countEl = document.getElementById('exampleCharCount');
  if (!countEl) return;

  const currentChars = currentExampleBlocks.reduce((sum, b) => sum + (b.content || '').length, 0);
  countEl.textContent = `${currentChars}/${MAX_TOTAL_CHARS}`;
}

function bindExampleModalEvents() {
  const modal = document.getElementById('exampleDialogueEditModal');
  if (!modal) return;

  // 닫기 버튼
  document.getElementById('closeExampleModalBtn')?.addEventListener('click', closeExampleModal);

  // 저장 버튼
  document.getElementById('saveExampleBtn')?.addEventListener('click', saveExample);

  // 타입 선택 탭
  document.getElementById('exampleTypeTabs')?.addEventListener('click', (e) => {
    const tab = e.target.closest('.intro-type-tab');
    if (tab && tab.dataset.exampleType) {
      setExampleType(tab.dataset.exampleType);
    }
  });

  // 블록 추가 버튼
  document.getElementById('addExampleBlockBtn')?.addEventListener('click', addExampleBlock);

  // 입력창 Enter 키 처리
  const input = document.getElementById('exampleComposerInput');
  if (input) {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        addExampleBlock();
      }
    });

    // textarea 자동 높이 조절
    input.addEventListener('input', () => {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 100) + 'px';
    });
  }

  // 삽입 버튼 이벤트
  const inputBar = modal.querySelector('.example-input-bar');
  if (inputBar) {
    inputBar.addEventListener('click', (e) => {
      const insertBtn = e.target.closest('.intro-insert-btn');
      if (insertBtn) {
        const insertText = insertBtn.dataset.insert;
        insertTextAtCursor(insertText);
      }
    });
  }

  // 블록 액션 이벤트 위임 및 빈 공간 클릭 처리
  const preview = document.getElementById('exampleDialoguePreview');
  if (preview) {
    preview.addEventListener('click', handlePreviewClick);
  }
}

function handlePreviewClick(e) {
  const actionEl = e.target.closest('[data-action]');
  if (actionEl) {
    e.stopPropagation();
    const idx = parseInt(actionEl.dataset.index, 10);
    const action = actionEl.dataset.action;

    switch (action) {
      case 'edit-example-block':
        editExampleBlock(idx);
        break;
      case 'move-example-up':
        moveExampleBlock(idx, -1);
        break;
      case 'move-example-down':
        moveExampleBlock(idx, 1);
        break;
      case 'delete-example-block':
        deleteExampleBlock(idx);
        break;
    }
    return;
  }

  // 블록 선택
  const wrapper = e.target.closest('.intro-block-wrapper');
  if (wrapper) {
    const idx = parseInt(wrapper.dataset.index, 10);
    selectExampleBlock(idx);
  } else {
    // 빈 공간 클릭 시 선택 해제
    deselectExampleBlock();
  }
}

function insertTextAtCursor(text) {
  const input = document.getElementById('exampleComposerInput');
  if (!input) return;

  const start = input.selectionStart;
  const end = input.selectionEnd;
  const value = input.value;

  input.value = value.substring(0, start) + text + value.substring(end);
  input.selectionStart = input.selectionEnd = start + text.length;
  input.focus();
}

export function closeExampleModal() {
  const modal = document.getElementById('exampleDialogueEditModal');
  if (!modal) return;

  modal.classList.remove('active');

  if (window.ModalManager) {
    window.ModalManager.remove('exampleDialogueEditModal', true);
  }

  setEditingExampleIndex(null);
  currentExampleBlocks = [];
  selectedExampleBlockIndex = null;
  editingBlockIndex = null;
}

// ============================================
// 블록 관리
// ============================================

function setExampleType(type) {
  currentExampleType = type;

  // 타입 탭 업데이트
  document.querySelectorAll('#exampleTypeTabs .intro-type-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.exampleType === type);
  });
}

function selectExampleBlock(idx) {
  selectedExampleBlockIndex = idx;
  document.querySelectorAll('#exampleDialoguePreview .intro-block-wrapper').forEach(el => {
    el.classList.remove('selected');
  });
  const selectedEl = document.querySelector(`#exampleDialoguePreview .intro-block-wrapper[data-index="${idx}"]`);
  if (selectedEl) {
    selectedEl.classList.add('selected');
  }
}

function deselectExampleBlock() {
  selectedExampleBlockIndex = null;
  document.querySelectorAll('#exampleDialoguePreview .intro-block-wrapper').forEach(el => {
    el.classList.remove('selected');
  });
}

function addExampleBlock() {
  const input = document.getElementById('exampleComposerInput');
  if (!input) return;

  const content = input.value.trim();
  if (!content) return;

  // 총 글자 수 체크
  const currentChars = currentExampleBlocks.reduce((sum, b) => sum + (b.content || '').length, 0);
  const otherDialoguesChars = getTotalExampleChars() - getCurrentEditingChars();

  if (currentChars + content.length + otherDialoguesChars > MAX_TOTAL_CHARS) {
    showToast(`대화 예시는 총 ${MAX_TOTAL_CHARS}자까지 작성할 수 있습니다`);
    return;
  }

  const newBlock = {
    type: currentExampleType,
    content: content
  };

  // 편집 중이면 원래 위치에 삽입
  if (editingBlockIndex !== null) {
    currentExampleBlocks.splice(editingBlockIndex, 0, newBlock);
    editingBlockIndex = null;
    // 버튼 스타일 복원
    const addBtn = document.getElementById('addExampleBlockBtn');
    if (addBtn) addBtn.classList.remove('editing');
  } else {
    currentExampleBlocks.push(newBlock);
  }

  input.value = '';
  input.style.height = 'auto';

  renderExamplePreview();
  updateExampleCharCount();

  // 스크롤 하단으로
  const preview = document.getElementById('exampleDialoguePreview');
  if (preview && editingBlockIndex === null) {
    preview.scrollTop = preview.scrollHeight;
  }
}

function editExampleBlock(index) {
  if (index < 0 || index >= currentExampleBlocks.length) return;

  const block = currentExampleBlocks[index];
  const input = document.getElementById('exampleComposerInput');

  if (input) {
    input.value = block.content;
    setExampleType(block.type);
    input.focus();
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 100) + 'px';
  }

  // 편집 위치 저장 후 삭제
  editingBlockIndex = index;
  currentExampleBlocks.splice(index, 1);

  // 버튼 스타일 변경
  const addBtn = document.getElementById('addExampleBlockBtn');
  if (addBtn) addBtn.classList.add('editing');

  renderExamplePreview();
  updateExampleCharCount();
  deselectExampleBlock();
}

function moveExampleBlock(index, direction) {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= currentExampleBlocks.length) return;

  const temp = currentExampleBlocks[index];
  currentExampleBlocks[index] = currentExampleBlocks[newIndex];
  currentExampleBlocks[newIndex] = temp;

  // 선택 상태를 새 인덱스로 업데이트
  selectedExampleBlockIndex = newIndex;

  renderExamplePreview();
}

function deleteExampleBlock(index) {
  if (index >= 0 && index < currentExampleBlocks.length) {
    currentExampleBlocks.splice(index, 1);
    renderExamplePreview();
    updateExampleCharCount();
    deselectExampleBlock();
  }
}

function renderExamplePreview() {
  const preview = document.getElementById('exampleDialoguePreview');
  if (!preview) return;

  preview.innerHTML = renderExampleBlocks();
}

function renderExampleBlocks() {
  if (currentExampleBlocks.length === 0) {
    return `
      <div class="intro-chat-empty">
        <p>아래에서 메시지를 추가해주세요</p>
      </div>
    `;
  }

  const charName = document.getElementById('editorCharName')?.value?.trim() || '점술가';
  const charImage = getMainImageUrl() || '';
  const totalBlocks = currentExampleBlocks.length;

  const getBlockActions = (idx) => `
    <div class="intro-block-actions-overlay">
      <button class="intro-action-btn" data-action="edit-example-block" data-index="${idx}" title="수정">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
      ${idx > 0 ? `
        <button class="intro-action-btn" data-action="move-example-up" data-index="${idx}" title="위로 이동">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </button>
      ` : ''}
      ${idx < totalBlocks - 1 ? `
        <button class="intro-action-btn" data-action="move-example-down" data-index="${idx}" title="아래로 이동">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      ` : ''}
      <button class="intro-action-btn delete" data-action="delete-example-block" data-index="${idx}" title="삭제">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  `;

  return currentExampleBlocks.map((block, idx) => {
    const content = formatExampleContent(block.content || '');
    const isSelected = selectedExampleBlockIndex === idx ? 'selected' : '';

    if (block.type === 'narration') {
      return `
        <div class="intro-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="narration-block">${content}</div>
        </div>
      `;
    } else if (block.type === 'dialogue') {
      return `
        <div class="intro-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="dialogue-block">
            <div class="dialogue-avatar">
              ${charImage ? `<img src="${charImage}" alt="${charName}" class="dialogue-avatar-img">` : '👤'}
            </div>
            <div class="dialogue-content">
              <div class="dialogue-speaker">${escapeHtml(charName)}</div>
              <div class="dialogue-bubble">
                <div class="dialogue-text">${content}</div>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (block.type === 'thought') {
      return `
        <div class="intro-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="thought-block">
            <div class="thought-avatar">
              ${charImage ? `<img src="${charImage}" alt="${charName}" class="dialogue-avatar-img">` : '💭'}
            </div>
            <div class="thought-content">
              <div class="thought-speaker">${escapeHtml(charName)}</div>
              <div class="thought-bubble">
                <div class="thought-text">${content}</div>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (block.type === 'user') {
      return `
        <div class="intro-block-wrapper user-block-wrapper ${isSelected}" data-index="${idx}">
          ${getBlockActions(idx)}
          <div class="message user">
            <div class="message-content">${content}</div>
          </div>
        </div>
      `;
    }
    return '';
  }).join('');
}

function formatExampleContent(text) {
  let escaped = escapeHtml(text);
  escaped = escaped.replace(/\{\{user\}\}/gi, '<span class="user-marker">{{user}}</span>');
  escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong class="bold-text">$1</strong>');
  escaped = escaped.replace(/\*([^*]+)\*/g, '<span class="action-text">$1</span>');
  return escaped;
}

// ============================================
// 저장
// ============================================

function saveExample() {
  if (currentExampleBlocks.length === 0) {
    showToast('최소 1개 이상의 메시지를 추가해주세요');
    return;
  }

  const editIndex = getEditingExampleIndex();
  const dialogue = {
    id: editIndex !== null ? getExampleDialogues()[editIndex]?.id : generateId(),
    blocks: [...currentExampleBlocks]
  };

  if (editIndex !== null) {
    // 편집 모드
    updateExampleDialogue(editIndex, dialogue);
  } else {
    // 새 추가
    addExampleDialogue(dialogue);
  }

  // 목록 업데이트
  renderExampleDialogueList();
  updateExampleDialogueTotalCount();
  updateTabCheckStatus();

  closeExampleModal();
  showToast(editIndex !== null ? '대화 예시가 수정되었습니다' : '대화 예시가 추가되었습니다');
}

// ============================================
// 삭제
// ============================================

export function deleteExample(index) {
  removeExampleDialogue(index);
  renderExampleDialogueList();
  updateExampleDialogueTotalCount();
  updateTabCheckStatus();
  showToast('대화 예시가 삭제되었습니다');
}

// ============================================
// 유틸리티
// ============================================

function getTotalExampleChars() {
  const dialogues = getExampleDialogues();
  let total = 0;
  dialogues.forEach(dialogue => {
    if (dialogue.blocks) {
      dialogue.blocks.forEach(block => {
        total += (block.content || '').length;
      });
    }
  });
  return total;
}

function getCurrentEditingChars() {
  const editIndex = getEditingExampleIndex();
  if (editIndex === null) return 0;

  const dialogues = getExampleDialogues();
  if (!dialogues[editIndex]) return 0;

  let chars = 0;
  dialogues[editIndex].blocks?.forEach(block => {
    chars += (block.content || '').length;
  });
  return chars;
}

function generateId() {
  return 'ex_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}
